import { useQueryClient } from "@tanstack/react-query"
import { useArticulosInvalidation } from "api/Articulo.hook"
import { useGetCampoPersonalizado } from "api/CampoPersonalizado.hook"
import { useIsFirstCesion } from "api/Cesion.hook"
import { useCuentasBancariasAll } from "api/CuentaBancaria.hook"
import { useEtiquetasEmpresa } from "api/Etiqueta.hook"
import { useIsFirstFactura } from "api/Factura.hook"
import { useImpuestoEmpresa } from "api/ImpuestoEmpresa.hook"
import { useIsFirstPagare } from "api/Pagare.hook"
import {
  useGetUsuarioByCurrentUser,
  useUsuarioEmpresaActiva,
} from "api/Usuario.hook"
import { addUsuarioEmpresa } from "api/Usuario.service"
import notification from "common/utils/notification"
import { resetFiltersSessionPrestamos } from "features/Contabilidad/pages/Prestamos/pages/PrestamoDetail/prestamoFilters"
import {
  fetchAutorizacionesPendding,
  fetchCesionesAny,
  fetchCessionPendding,
  fetchMiUsuarioYEmpresas,
} from "features/User/redux/slices/userSlice"
import UserService from "features/User/services/UserService"
import { setCertificadoValidation } from "features/Validaciones/redux/slices/validationsSlice"
import { useAsociarDocumentosInvalidation } from "hooks/useAsociarDocumentos"
import { useDispatch } from "hooks/useDispatch"
import { useUsuarioByCurrent } from "hooks/useUsuarioByCurrent"
import { useTranslation } from "react-i18next"

const userService = new UserService()

const useCambiarEmpresa = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { usuarioInvalidation } = useGetUsuarioByCurrentUser()
  const cuentasContablesEmpresasInvalidation = () =>
    queryClient.invalidateQueries({ queryKey: ["cuentasContablesEmpresas"] })
  const { updateAllArticulosQueries } = useArticulosInvalidation()
  const { usuarioInternoInvalidation } = useUsuarioByCurrent()
  const {
    activeCompany: empresaActiva,
    activeCompanyIsLoading: isCambiandoEmpresaActiva,
    activeCompanyInvalidation,
  } = useUsuarioEmpresaActiva()
  const { invalidateIsFirstCesion } = useIsFirstCesion()
  const { invalidateIsFirstFactura } = useIsFirstFactura()
  const { invalidateIsFirstPagare } = useIsFirstPagare()
  const { deleteInfiniteQueryAsociarDoc } = useAsociarDocumentosInvalidation()
  const { etiquetasEmpresaInvalidation } = useEtiquetasEmpresa()
  const { impuestoEmpresaInvalidation } = useImpuestoEmpresa()
  const { campoPersonalizadoInvalidation } = useGetCampoPersonalizado()
  const { invalidateCuentaBancariaAll } = useCuentasBancariasAll({})
  const dispatch = useDispatch()

  const setEmpresaActiva = async (empresaID: $TSMigrationFixMe) => {
    sessionStorage.setItem("empresaActivaChanging", "true")
    await userService.setEmpresaActiva(empresaID)
    await activeCompanyInvalidation()
    await usuarioInvalidation()
    await impuestoEmpresaInvalidation()
    etiquetasEmpresaInvalidation()
    dispatch(fetchMiUsuarioYEmpresas())
    dispatch(fetchCessionPendding())
    dispatch(fetchAutorizacionesPendding())
    dispatch(fetchCesionesAny())
    dispatch(setCertificadoValidation(false))
    resetFiltersSessionPrestamos()
    cuentasContablesEmpresasInvalidation()
    usuarioInternoInvalidation()
    deleteInfiniteQueryAsociarDoc()
    updateAllArticulosQueries()
    campoPersonalizadoInvalidation()
    invalidateCuentaBancariaAll()
    invalidateIsFirstCesion()
    invalidateIsFirstFactura()
    invalidateIsFirstPagare()
    sessionStorage.removeItem("empresaActivaChanging")
  }

  const crearNuevaEmpresa = async (newEmpresa: $TSMigrationFixMe) => {
    const resp = await addUsuarioEmpresa(newEmpresa)

    if (resp.status === 200) {
      notification.success({ body: `${t("successMsg.createEmpresa")}` })
    } else {
      notification.errorServidor({
        body: `${t("successMsg.errorCreateEmpresa")}`,
      })
    }

    await activeCompanyInvalidation()
    await impuestoEmpresaInvalidation()
    dispatch(fetchAutorizacionesPendding())
    dispatch(fetchMiUsuarioYEmpresas())
    dispatch(fetchCessionPendding())
    dispatch(fetchCesionesAny())
    cuentasContablesEmpresasInvalidation()
    usuarioInternoInvalidation()
    deleteInfiniteQueryAsociarDoc()
    updateAllArticulosQueries()
    campoPersonalizadoInvalidation()
    invalidateCuentaBancariaAll()
    invalidateIsFirstCesion()
    invalidateIsFirstFactura()
    invalidateIsFirstPagare()
  }

  return {
    isCambiandoEmpresaActiva,
    setEmpresaActiva,
    crearNuevaEmpresa,
    empresaActiva,
  }
}

export default useCambiarEmpresa
